/**
 * One station's house name and city, beside the source's (SA-1054 §5.3). The
 * page keys this component by the station's ident, so typed text never carries
 * over to another station. Save sends both fields; an empty one clears.
 */
import { useState, type FormEvent } from 'react'
import {
  Alert, AlertDescription, Button, Input,
  Modal, ModalBody, ModalContent, ModalDescription, ModalFooter, ModalHeader, ModalTitle,
} from '@/components/ui'
import { useSaveAirportHouse } from '@/hooks/useAirportHouse'
import type { Airport } from '@/types/airports'
import * as text from './airportsText'

/** The server's `HOUSE_MAX`. */
export const HOUSE_MAX = 255

function SourceValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="mb-1.5 text-sm font-medium text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  )
}

export function AirportHouseDialog({ airport, onClose }: { airport: Airport | null; onClose: () => void }) {
  const [houseName, setHouseName] = useState(airport?.houseName ?? '')
  const [houseCity, setHouseCity] = useState(airport?.houseCity ?? '')
  const [error, setError] = useState<string | null>(null)
  const save = useSaveAirportHouse()

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!airport) return
    setError(null)
    try {
      await save.mutateAsync({ ident: airport.ident, houseName, houseCity })
      onClose()
    } catch (err) {
      setError(text.saveErrorText(err))
    }
  }

  return (
    <Modal open={airport !== null} onOpenChange={(open) => !open && onClose()}>
      {airport && (
        <ModalContent size="md">
          <ModalHeader>
            <ModalTitle>{text.dialogTitle(airport)}</ModalTitle>
            <ModalDescription>{text.EMPTY_FIELD_HINT}</ModalDescription>
          </ModalHeader>
          <form onSubmit={onSubmit}>
            <ModalBody className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <SourceValue label={text.SOURCE_NAME} value={airport.name} />
                <Input
                  label={text.HOUSE_NAME} value={houseName} maxLength={HOUSE_MAX}
                  onChange={(e) => setHouseName(e.target.value)}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <SourceValue label={text.SOURCE_CITY} value={text.sourceCity(airport)} />
                <Input
                  label={text.HOUSE_CITY} value={houseCity} maxLength={HOUSE_MAX}
                  onChange={(e) => setHouseCity(e.target.value)}
                />
              </div>
              {error && (
                <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      )}
    </Modal>
  )
}
